import classNames from "classnames";
import React from "react";

import { ConfigData } from "../../config";
import { FlagType } from "../../model";
import {
  NumberedEntry,
  placeEntries,
  ringLabelRadius,
  zoomGeometry,
} from "../../quadrantZoom";
import "./quadrant-chart.scss";

/** User units of the drawing. The SVG scales itself to its column. */
const SIZE = 520;
/** Room under the bottom axis for the ring labels. */
const LABEL_BAND = 34;

type Props = {
  entries: NumberedEntry[];
  config: ConfigData;
  quadrant: string;
  /** Number of the entry the pointer is on, in the chart or in the list. */
  highlighted?: number;
  onHighlight: (n?: number) => void;
  onSelect: (entry: NumberedEntry) => void;
};

/**
 * One quadrant of the radar, on a quarter circle, blips numbered.
 *
 * The centre of the radar is the bottom right corner: rings widen up and to the
 * left, and their labels read from the centre outwards along the bottom axis —
 * Adopt nearest the centre, Hold farthest. See `quadrantZoom.ts` for why the
 * quarter is used whatever the number of segments the radar declares.
 *
 * Flags are drawn as rings around the blip rather than as the triangle and
 * diamond of the full chart: a number has to stay readable inside its shape, and
 * the legend sits right underneath to say what the rings mean.
 */
export default function QuadrantChart({
  entries,
  config,
  quadrant,
  highlighted,
  onHighlight,
  onSelect,
}: Props) {
  // The geometry is derived inside the memo so that the entries and the radar's
  // own ring scale are the only inputs the layout depends on.
  const { geometry, placed } = React.useMemo(() => {
    const geometry = zoomGeometry(config, SIZE);
    return { geometry, placed: placeEntries(entries, geometry) };
  }, [entries, config]);
  const colour =
    config.quadrantsMap[quadrant]?.colour || "var(--logo-deepblue)";

  return (
    <svg
      className="quadrant-chart"
      viewBox={`0 0 ${SIZE + 4} ${SIZE + LABEL_BAND}`}
      role="img"
      aria-label={`${entries.length} entries plotted by ring`}
    >
      {/* Ring bands, from the outermost so the inner ones paint over them. */}
      {geometry.ringRadii
        .map((radius, index) => ({ radius, index }))
        .reverse()
        .map(({ radius, index }) => (
          <path
            key={index}
            className="quadrant-chart__band"
            d={`M ${
              SIZE - radius
            } ${SIZE} A ${radius} ${radius} 0 0 1 ${SIZE} ${
              SIZE - radius
            } L ${SIZE} ${SIZE} Z`}
            fill={colour}
            fillOpacity={0.04 + index * 0.015}
            stroke={colour}
            strokeOpacity={0.28}
          />
        ))}

      {/* The two straight edges of the quarter. */}
      <path
        className="quadrant-chart__axis"
        d={`M ${
          SIZE - geometry.ringRadii[geometry.ringRadii.length - 1]
        } ${SIZE}
            L ${SIZE} ${SIZE}
            L ${SIZE} ${
          SIZE - geometry.ringRadii[geometry.ringRadii.length - 1]
        }`}
        fill="none"
      />

      {config.rings.map((ring, index) => (
        <text
          key={ring}
          className="quadrant-chart__ring-label"
          x={SIZE - ringLabelRadius(geometry, index)}
          y={SIZE + 22}
          textAnchor="middle"
        >
          {ring}
        </text>
      ))}

      {placed.map((entry) => {
        const isHighlighted = highlighted === entry.number;
        const flag = entry.item.flag;

        return (
          <g
            key={entry.number}
            className={classNames("quadrant-chart__blip", {
              "is-highlighted": isHighlighted,
              "is-dimmed": highlighted !== undefined && !isHighlighted,
            })}
            onMouseEnter={() => onHighlight(entry.number)}
            onMouseLeave={() => onHighlight(undefined)}
            onClick={() => onSelect(entry)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(entry);
              }
            }}
            aria-label={`${entry.number}. ${entry.item.title}, ${entry.item.ring}`}
          >
            {/* New: a halo outside the blip. Moved: a ring inside it. */}
            {flag === FlagType.new && (
              <circle
                className="quadrant-chart__halo"
                cx={entry.point.x}
                cy={entry.point.y}
                r={geometry.blipRadius + 3.5}
                fill="none"
                stroke={colour}
                strokeOpacity={0.45}
              />
            )}
            <circle
              cx={entry.point.x}
              cy={entry.point.y}
              r={geometry.blipRadius}
              fill={colour}
            />
            {flag === FlagType.changed && (
              <circle
                cx={entry.point.x}
                cy={entry.point.y}
                r={geometry.blipRadius - 3}
                fill="none"
                stroke="#ffffff"
                strokeOpacity={0.9}
              />
            )}
            <text
              className="quadrant-chart__number"
              x={entry.point.x}
              y={entry.point.y}
              textAnchor="middle"
              dy="0.34em"
              fill={config.quadrantsMap[quadrant]?.txtColour || "#ffffff"}
            >
              {entry.number}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
